import { Portal } from 'components/Portal/Portal'
import { SelectionCount } from 'components/ImagePickerModal/SelectionCount'
import { ConfirmSelectionButton } from 'components/ImagePickerModal/ConfirmSelectionButton'
import { DeselectAllButton } from 'components/ImagePickerModal/DeselectAllButton'
import { MutableRefObject, useState } from 'react'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { MediaFiles } from 'components/ImagePickerModal/BrandKits/MediaFiles'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { Chip } from 'components/Chip/Chip'
import { BrandKitLogo } from 'screens/BrandKit/BrandKitLogo/BrandKitLogo'
import { ScrollContainer } from 'components/ImagePickerModal/ScrollContainer/ScrollContainer'
import { Grid } from 'components/ImagePickerModal/Grid/Grid'
import { ImageThumbnail } from 'components/ImagePickerModal/ImageThumbnail/ImageThumbnail'
import Styles from './BrandKits.module.scss'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { Heading } from 'blaze-ui/Heading/Heading'
import { pluralize } from 'utils'

interface Props {
  actionsContainerRef?: MutableRefObject<HTMLDivElement | null>
  initialBrandKitId: number
}

export const BrandKits = ({ actionsContainerRef, initialBrandKitId }: Props) => {
  const context = useImagePickerContext()
  const { selectedImages, toggleSelectedImage, onConfirm } = context
  const { isMobile } = useResponsiveDesign()
  const { data } = useBrandKits({
    select: (data) => data.filter((brandKit) => brandKit.media_files.length > 0),
    cacheTime: 1000 * 60 * 5,
  })
  const [selectedBrandKitId, setSelectedBrandKitId] = useState<number | undefined>(initialBrandKitId)

  return (
    <div className={Styles.brandKits} data-testid="brandKits-provider">
      {actionsContainerRef && (
        <Portal container={actionsContainerRef}>
          {isMobile && (
            <>
              <SelectionCount />
              <ConfirmSelectionButton
                onPress={() => onConfirm(selectedImages, context)}
                isDisabled={selectedImages.length === 0}
              />
            </>
          )}
          {!isMobile && selectedImages.length > 0 && (
            <>
              <SelectionCount />
              <DeselectAllButton />
              <ConfirmSelectionButton onPress={() => onConfirm(selectedImages, context)} />
            </>
          )}
        </Portal>
      )}
      <div className={Styles.header}>
        <Heading level={5}>Select a Brand Kit</Heading>
        <div className={Styles.pills}>
          {data?.map((brandKit) => (
            <Chip
              icon={() => <BrandKitLogo brandKit={brandKit} size={16} />}
              deletable={false}
              key={brandKit.id}
              isSelected={selectedBrandKitId === brandKit.id}
              onChange={(isSelected) => isSelected && setSelectedBrandKitId(brandKit.id)}
            >
              {brandKit.name}
            </Chip>
          ))}
        </div>
      </div>
      <div className={Styles.images}>
        <MediaFiles brandKitId={selectedBrandKitId}>
          {({ mediaFiles, hasNextPage, fetchNextPage }) => (
            <>
              <Heading level={5}>
                {mediaFiles.length} {pluralize('Image', mediaFiles.length)}
              </Heading>
              <ScrollContainer>
                <Grid.Root breakpointCols={isMobile ? 2 : 4}>
                  {mediaFiles.map((mediaFile) => (
                    <Grid.Thumbnail key={mediaFile.id}>
                      <ImageThumbnail
                        image={{ ...mediaFile, url: mediaFile.thumbnail_url as string }}
                        onToggleSelect={toggleSelectedImage}
                        isSelected={selectedImages.some((i) => i.url === mediaFile.thumbnail_url)}
                        duration={mediaFile.duration}
                      />
                    </Grid.Thumbnail>
                  ))}
                </Grid.Root>
                {hasNextPage && (
                  <div className={Styles.infiniteLoader}>
                    <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage} />
                  </div>
                )}
              </ScrollContainer>
            </>
          )}
        </MediaFiles>
      </div>
    </div>
  )
}
